import { useLayoutEffect, useRef, useState } from 'react'
import { useDrag } from 'react-dnd'
import { useNavigate } from 'react-router-dom'
import * as Accordion from '@radix-ui/react-accordion'
import styled, { css, keyframes } from 'styled-components'
import { DEFAULT_FOLDER_ID } from '../../constants'
import useOverviewContext from '../../context/OverviewContextProvider'
import { useIsMobile } from '../../hooks'
import useGetViewItems from '../../hooks/useGetViewItems'
import useGetVisibleItemCount, { PAGE_SIZE } from '../../hooks/useGetVisibleItemCount'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { useRemoveView } from '../../services/api/overview.hooks'
import { Border, Colors, Spacing } from '../../styles'
import { pointerQuery } from '../../styles/dimensions'
import { TLogoImage, icons, logos } from '../../styles/images'
import { Motion } from '../../styles/motion'
import {
    didOverviewViewBecomeEmptyAfterCompletion,
    getFirstTaskInOverviewView,
    getOverviewOpenIdsAfterViewEmpties,
    isOverviewViewNonEmpty,
} from '../../utils/overview'
import { DropItem, DropType, TOverviewView, TTaskV4 } from '../../utils/types'
import Domino from '../atoms/Domino'
import ReorderDropContainer from '../atoms/ReorderDropContainer'
import GTButton from '../atoms/buttons/GTButton'
import GTDropdownMenu from '../radix/GTDropdownMenu'
import GTAccordionHeader from './AccordionHeader'
import AuthBanner from './AuthBanner'
import { PaginateTextButton } from './styles'
import { OverviewCardOrderActions } from './useOverviewCardOrder'

const AccordionContainer = styled(Accordion.Item)`
    width: 100%;
    margin-bottom: ${Spacing._8};
`
const contentOpen = keyframes`
    from {
        height: var(--overview-accordion-start-height, 0px);
        opacity: var(--overview-accordion-start-opacity, 0);
        clip-path: inset(0);
    }
    to { height: var(--radix-accordion-content-height); opacity: 1; clip-path: inset(0); }
`
const contentClosed = keyframes`
    from {
        height: var(--overview-accordion-start-height, var(--radix-accordion-content-height));
        opacity: var(--overview-accordion-start-opacity, 1);
        clip-path: inset(0);
    }
    to { height: 0; opacity: 0; clip-path: inset(0); }
`
const Grip = styled(Domino)`
    display: flex;
    align-items: center;
    margin: -${Spacing._4} ${Spacing._4} -${Spacing._4} -${Spacing._8};
    cursor: grab;
    /* display: flex above would override Domino's own touch rule, so repeat it: drag never fires on touch. */
    ${pointerQuery.noHover} {
        display: none;
    }
`
const CardMenu = styled.div`
    display: flex;
    align-items: center;
    margin: -${Spacing._4} -${Spacing._4} -${Spacing._4} ${Spacing._4};
`
const CardHeader = styled.div<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 0 ${Spacing._12};
    background-color: ${Colors.background.white};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-bottom-color: ${({ $isOpen }) => ($isOpen ? Colors.background.hairline : Colors.background.border)};
    border-radius: ${({ $isOpen }) =>
        $isOpen ? `${Border.radius.medium} ${Border.radius.medium} 0 0` : Border.radius.medium};

    /* AccordionHeader owns the semantic h3/trigger; this shell owns the shared card chrome. */
    & > h3 {
        flex: 1;
        min-width: 0;
        width: auto;
        padding: 0;
        border: 0;
        border-radius: 0;
    }
`
const ListContent = styled(Accordion.Content)<{ $disableMotion: boolean }>`
    /* Clip descendants to the changing box only while the height animation runs. Once open, the
       clip-path is removed with the animation so the task drag grip can sit outside its row. */
    overflow: visible;
    will-change: height, opacity, clip-path;

    ${({ $disableMotion }) =>
        !$disableMotion &&
        css`
            &[data-state='open'] {
                animation: ${contentOpen} ${Motion.enter};
            }
            &[data-state='closed'] {
                animation: ${contentClosed} ${Motion.exit};
            }
        `}
`
const ListContentInner = styled.div`
    padding: ${Spacing._8};
    background-color: ${Colors.background.white};
    border-radius: 0 0 ${Border.radius.medium} ${Border.radius.medium};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-top: 0;
    box-sizing: border-box;
`

export const getOverviewAccordionHeaderIcon = (logo: TLogoImage, sectionId?: string) => {
    if (logo !== 'generaltask') return logos[logo]
    return sectionId === DEFAULT_FOLDER_ID ? icons.inbox : icons.folder
}

export const getOverviewViewIcon = (list: TOverviewView) =>
    list.type === 'routine' ? icons.list_ol : getOverviewAccordionHeaderIcon(list.logo, list.task_section_id)

// The grip is its own drag source: dragging the header itself still schedules the list on the calendar.
const CardGrip = ({ list }: { list: TOverviewView }) => {
    const isReadOnly = useIsReadOnly()
    const [, drag] = useDrag(
        () => ({ type: DropType.OVERVIEW_VIEW, item: { id: list.id, view: list }, canDrag: !isReadOnly }),
        [list, isReadOnly]
    )
    if (isReadOnly) return null
    return (
        <span title={`Drag ${list.name}`} onClick={(event) => event.stopPropagation()}>
            <Grip ref={drag} />
        </span>
    )
}
// Menu clicks bubble through the portal to the card trigger, so stop them here or they toggle the card.
const CardMenuButton = ({ list, actions }: { list: TOverviewView; actions: OverviewCardOrderActions }) => {
    const { mutate: removeView } = useRemoveView()
    const isReadOnly = useIsReadOnly()
    return (
        <CardMenu onClick={(event) => event.stopPropagation()}>
            <GTDropdownMenu
                hideCheckmark
                align="end"
                items={[
                    [
                        {
                            label: 'Move to top',
                            icon: icons.arrow_up_to_line,
                            disabled: !actions.moveToTop,
                            onClick: actions.moveToTop,
                        },
                        { label: 'Move up', icon: icons.arrow_up, disabled: !actions.moveUp, onClick: actions.moveUp },
                        {
                            label: 'Move down',
                            icon: icons.arrow_down,
                            disabled: !actions.moveDown,
                            onClick: actions.moveDown,
                        },
                    ],
                    [
                        {
                            label: 'Hide from Overview',
                            icon: icons.eye_slash,
                            disabled: isReadOnly,
                            onClick: () => removeView({ id: list.id }, list.optimisticId),
                        },
                    ],
                ]}
                trigger={
                    <GTButton styleType="icon" icon={icons.ellipsisVertical} tooltipText={`Options for ${list.name}`} />
                }
            />
        </CardMenu>
    )
}

interface AccordionItemProps {
    list: TOverviewView
    lists?: TOverviewView[]
    activeMeetingPreparationTasks?: TTaskV4[]
    showCollapsedPreview?: boolean
    // Daily Overview passes this so the card can be reordered where it sits.
    order?: {
        index: number
        actions: OverviewCardOrderActions
        onDrop: (item: DropItem, slot: number) => void
    }
}
const AccordionItem = ({
    list,
    lists,
    activeMeetingPreparationTasks = [],
    showCollapsedPreview = false,
    order,
}: AccordionItemProps) => {
    const ViewItems = useGetViewItems(list)
    const { setOpenListIds, openListIds, motionlessListIds, announceListOpened } = useOverviewContext()
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    const isOpen = openListIds.includes(list.id)
    // This card stays mounted when its accordion body closes, so an explicit routine expansion
    // survives closing and reopening the card but resets on the next screen mount.
    const [showCompletedRoutineItems, setShowCompletedRoutineItems] = useState(false)
    const activeMeetingPreparationCount = activeMeetingPreparationTasks.length
    const isNonEmpty = isOverviewViewNonEmpty(list, activeMeetingPreparationCount)
    const previousList = useRef(list)
    const previousActiveMeetingPreparationCount = useRef(activeMeetingPreparationCount)
    const becameEmpty =
        Boolean(lists) &&
        didOverviewViewBecomeEmptyAfterCompletion(
            previousList.current,
            list,
            previousActiveMeetingPreparationCount.current,
            activeMeetingPreparationCount
        )
    // Keep the populated body around while Radix plays its close animation. The task component keeps
    // its already-collapsed row state, while the input and card padding can now leave with the card.
    const closingListRef = useRef<TOverviewView>()
    // A failed first create removes its only optimistic row; keep that manually opened capture card open.
    const emptiedAfterOptimisticCreate = Boolean(
        becameEmpty &&
            closingListRef.current?.view_items.length &&
            closingListRef.current.view_items.every((item) => item.optimisticId)
    )
    const shouldCloseAfterEmpty = becameEmpty && !emptiedAfterOptimisticCreate
    if (isNonEmpty) closingListRef.current = list
    else if (isOpen && !shouldCloseAfterEmpty) closingListRef.current = undefined
    const renderedList = !isNonEmpty && (!isOpen || shouldCloseAfterEmpty) ? closingListRef.current ?? list : list

    useLayoutEffect(() => {
        // The completed row has already collapsed for the full completion duration. Close the
        // remaining card body without briefly rendering the empty-list message.
        if (shouldCloseAfterEmpty && lists) {
            let nextIds: string[] | undefined
            let openedView: TOverviewView | undefined
            setOpenListIds((ids) => {
                const updatedIds = getOverviewOpenIdsAfterViewEmpties(
                    lists,
                    list.id,
                    ids,
                    activeMeetingPreparationCount
                )
                if (updatedIds === ids) return ids
                nextIds = updatedIds
                const openedId = updatedIds.find((id) => !ids.includes(id))
                openedView = lists.find((candidate) => candidate.id === openedId)
                return updatedIds
            })
            if (nextIds) {
                if (openedView) announceListOpened(openedView.name)

                if (!isMobile) {
                    const nextTaskView = lists.find(
                        (candidate) =>
                            nextIds?.includes(candidate.id) &&
                            isOverviewViewNonEmpty(candidate, activeMeetingPreparationCount) &&
                            Boolean(getFirstTaskInOverviewView(candidate, activeMeetingPreparationTasks))
                    )
                    const nextTask = nextTaskView
                        ? getFirstTaskInOverviewView(nextTaskView, activeMeetingPreparationTasks)
                        : undefined
                    if (nextTaskView && nextTask) navigate(`/overview/${nextTaskView.id}/${nextTask.id}`)
                }
            }
        }
        previousList.current = list
        previousActiveMeetingPreparationCount.current = activeMeetingPreparationCount
    }, [
        activeMeetingPreparationCount,
        activeMeetingPreparationTasks,
        announceListOpened,
        isMobile,
        list,
        list.id,
        lists,
        navigate,
        setOpenListIds,
        shouldCloseAfterEmpty,
    ])

    const [visibleItemsCount, setVisibleItemsCount] = useGetVisibleItemCount(list, list.id)

    const nextPageLength = Math.min(renderedList.view_items.length - visibleItemsCount, PAGE_SIZE)

    const card = (
        <AccordionContainer value={list.id} data-testid="overview-card">
            <CardHeader $isOpen={isOpen}>
                {order && <CardGrip list={list} />}
                <GTAccordionHeader
                    list={list}
                    isOpen={isOpen}
                    activeMeetingPreparationTasks={activeMeetingPreparationTasks}
                    showCollapsedPreview={showCollapsedPreview}
                />
                {order && <CardMenuButton list={list} actions={order.actions} />}
            </CardHeader>
            <ListContent
                $disableMotion={motionlessListIds.includes(list.id)}
                data-overview-accordion-content={list.id}
                onAnimationEnd={(event) => {
                    if (event.target === event.currentTarget && event.currentTarget.dataset.state === 'closed')
                        closingListRef.current = undefined
                }}
            >
                <ListContentInner>
                    {renderedList.is_linked ? (
                        <>
                            <ViewItems
                                view={renderedList}
                                visibleItemsCount={visibleItemsCount}
                                hideHeader
                                showCompletedRoutineItems={showCompletedRoutineItems}
                                onToggleCompletedRoutineItems={() =>
                                    setShowCompletedRoutineItems((isShowing) => !isShowing)
                                }
                            />
                            {renderedList.type !== 'routine' && visibleItemsCount < renderedList.view_items.length && (
                                <PaginateTextButton
                                    onClick={() => setVisibleItemsCount(visibleItemsCount + nextPageLength)}
                                >
                                    View more ({nextPageLength})
                                </PaginateTextButton>
                            )}
                        </>
                    ) : (
                        renderedList.sources.map((source) => (
                            <AuthBanner
                                key={source.name}
                                authorizationUrl={source.authorization_url}
                                name={source.name}
                                logo={renderedList.logo}
                                hasBorder={true}
                            />
                        ))
                    )}
                </ListContentInner>
            </ListContent>
        </AccordionContainer>
    )
    if (!order) return card
    return (
        <ReorderDropContainer index={order.index} acceptDropType={DropType.OVERVIEW_VIEW} onReorder={order.onDrop}>
            {card}
        </ReorderDropContainer>
    )
}

export default AccordionItem
